import {gsap} from "gsap";

/**
 * Basic wrapper for Spine animations so that you can use them in gsap easier.
 */
export class SpineGSAPWrapper {

    /**
     * Returns gsap.core.Timeline with a call to the targets setAnimation.
     * The timeline is padded with a duration that matches the Spine animation.
     *
     * Note on duration:
     * Duration is calculated bases on the length of the animation and loop count.
     *
     * loopCount = 0:
     *  duration of tween is equal to one play though
     *
     *  loopCount > 0:
     *   duration + duration * loopCount
     *
     * loopCount = -1: Loop for ever,
     *  duration of tween is equal to one play though
     *
     *
     * @param target The spine object you want to animate
     * @param trackIndex Channel to play animation in.
     * @param animationName Name of the animation, null will call target.state.setEmptyAnimation()
     * @param loopCount Number of times the animation will loop (after initial play through). -1 = Loop forever.
     * @param animationAlpha See Spine doc. But roughly how much the animation should effect current animation.
     */


    public static createTimeline(target:PIXI.spine.Spine, trackIndex:number, animationName:string | null, loopCount:number = 0, animationAlpha:number = 1):gsap.core.Timeline {

        const tl:gsap.core.Timeline = gsap.timeline();

        if(animationName == null) {
            SpineGSAPWrapper.clearAnimation(target, trackIndex);
            return tl;
        }

        const animation:PIXI.spine.core.Animation = target.spineData.findAnimation(animationName);
        if(!animation) {
            throw new Error(`No animation named ${animationName}`);
        }
        const spineShouldLoop:boolean = loopCount != 0;
        const duration:number = Math.max(animation.duration, animation.duration + (animation.duration * loopCount));

        tl.add(() => SpineGSAPWrapper.setAnimation(target, trackIndex, animation, spineShouldLoop, animationAlpha));
        tl.add(gsap.to(this, {duration : duration}));
        if(loopCount > 0) {
            tl.add(() => SpineGSAPWrapper.stopLoop(target, trackIndex, animation));
        }

        return tl;
    }

    public static clearAnimation(target:PIXI.spine.Spine, trackIndex:number) {
        target.state.setEmptyAnimation(trackIndex, 0);
    }

    public static clearAllTracks(target:PIXI.spine.Spine) {
        for(let track of target.state.tracks) {
            if(track != undefined) {
                target.state.setEmptyAnimation(track.trackIndex, 0);
            }
        }
    }


    private static stopLoop(target:PIXI.spine.Spine, trackIndex:number, animation:PIXI.spine.core.Animation) {
        const trackEntry:PIXI.spine.core.TrackEntry = target.state.getCurrent(trackIndex);
        if(trackEntry.animation == animation) {
            trackEntry.loop = false;
        }
    }

    public static animationTimeDuration(target:PIXI.spine.Spine,animName:string):number{
        return target.skeleton.data.findAnimation(animName)?.duration;
    }

    private static setAnimation(target:PIXI.spine.Spine, trackIndex:number, animation:PIXI.spine.core.Animation, loop:boolean, animationAlpha:number) {
        const trackEntry:PIXI.spine.core.TrackEntry = target.state.setAnimationWith(trackIndex, animation, loop);
        trackEntry.alpha = animationAlpha;
    }


    /**
     * This is a new aproach to this problem, with the focus being to really tie the to kinds of animations together to make a "gsap safe"
     * spine animation that actually follows the playhead of any parent timeline and supports other gsap features, such as reverse(), seek() etc.
     * Instead of just starting and stopping animations at different times.
     *
     * Caveat:
     * Does only set a new spine animation at beginning and end of the timeline.
     * So if you try to play the timeline from, say halfway into the animation, and this is not the already set spine animation.
     * It will play the current set spine animation. So playing animations not completely from the start or end (reverse(0)), will probably
     * cause you lots of headache.
     *
     * Easing do work. But be a bit cautious with easing that have overshoot. E.g Back, or Elastic etc.
     * Because we are only animating the play head on the spine animation, there is no "overshoot". You cant play the animation longer than it is.
     *
     * !!! YOU NEED TO SET "autoUpdate = false" on your spine object.
     *
     *
     * @param spine PIXI.spineSpine object
     * @param animationName
     * @param trackIndex
     * @param animationAlpha
     * @private
     */
    public static createTimelineForAnimation(spine: PIXI.spine.Spine, trackIndex: number, animationName: string, loop: boolean, animationAlpha: number = 1, mixDuration: number = 0, resetOnComplete: boolean = true): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
            repeat: loop ? -1 : 0
        });

        if (!spine.spineData || !spine.state) {
            return tl;
        }

        const animation: PIXI.spine.core.Animation = spine.spineData.findAnimation(animationName);
        if (!animation) {
            console.error(`No animation named ${animationName}`);

            return tl;
        }

        spine.autoUpdate = false;

        const setAnimation: () => void = () => {
            if (spine.state) {
                const trackEntry: PIXI.spine.core.TrackEntry = spine.state.setAnimationWith(trackIndex, animation, false);
                trackEntry.alpha = animationAlpha;
                trackEntry.mixDuration = mixDuration;
            }
        };

        const target = {
            playHead: 0,
            spine: spine,
            prevTime: 0
        };

        const updateFunc: () => void = () => {
            if (spine && target && spine.state) {
                const now = target.playHead;
                spine.update(now - target.prevTime);
                target.prevTime = now;
            }
        };

        tl.add(() => {
            target.prevTime = 0;
            setAnimation();
        });
        tl.fromTo(target, {playHead: 0}, {
            duration: animation.duration,
            playHead: animation.duration,
            onUpdate: updateFunc,
            ease: "none"
        });
        if (resetOnComplete) {
            tl.add(() => {
                target.prevTime = 0;
                setAnimation();
            });
        }
        tl.to(target, {duration: 0.0001}); //Needed so gsap does not skip "setAnimation" during reverse play.
        tl.add(() => {
            target.playHead = animation.duration;
            updateFunc();
        });

        return tl;
    }

    public static resetSpineItem(item: PIXI.spine.Spine): void {
        item.state.clearTracks();
        item.skeleton.setToSetupPose();
        item.skeleton.setSlotsToSetupPose();
        item.updateTransform();
        item.state.setEmptyAnimation(0, 0);
    }
}